import { FC } from "react";
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Description,
  Download,
} from "@mui/icons-material";
import DeleteIcon from "@/assets/svgs/garbage.svg";
import {
  getDocumentTypeFullLabel,
  getStatusLabel,
} from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

const COLUMN_COUNT = 8;

export interface UnclusteredRowProps {
  documents: CustomsDocument[];
  isExpanded: boolean;
  onToggle: () => void;
  onDownload: (doc: CustomsDocument) => void;
  onDelete: (doc: CustomsDocument) => void;
  loadingStates: { delete: string[]; download: string[] };
  onDocumentClick?: (doc: CustomsDocument) => void;
}

export const UnclusteredRow: FC<UnclusteredRowProps> = ({
  documents,
  isExpanded,
  onToggle,
  onDownload,
  onDelete,
  loadingStates,
  onDocumentClick,
}) => {
  if (documents.length === 0) return null;

  return (
    <>
      <TableRow
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          backgroundColor: isExpanded ? tokens.bg.muted : tokens.bg.hover,
          "&:hover": {
            backgroundColor: tokens.bg.muted,
          },
          "& > td": {
            borderBottom: isExpanded ? "none" : `1px solid ${tokens.border.light}`,
          },
        }}
      >
        {/* Expand Icon */}
        <TableCell sx={{ width: 48, p: 1, pl: 1.5 }}>
          <IconButton size="small" sx={{ p: 0.5 }}>
            {isExpanded ? (
              <KeyboardArrowDown sx={{ color: tokens.text.secondary, fontSize: 20 }} />
            ) : (
              <KeyboardArrowRight sx={{ color: tokens.text.secondary, fontSize: 20 }} />
            )}
          </IconButton>
        </TableCell>

        {/* Label */}
        <TableCell sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>
          Unclustered Documents
        </TableCell>

        {/* Empty cell for Identifiers column */}
        <TableCell />

        {/* Document Count */}
        <TableCell align="center">
          <Chip
            label={documents.length}
            size="small"
            sx={{
              height: 22,
              minWidth: 28,
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              bgcolor: tokens.bg.muted,
              color: tokens.text.body,
            }}
          />
        </TableCell>

        {/* Empty cells for document types, status, audit, actions */}
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
      </TableRow>

      {/* Expanded Document List */}
      {isExpanded && (
        <TableRow>
          <TableCell
            colSpan={COLUMN_COUNT}
            sx={{ p: 0, borderBottom: `1px solid ${tokens.bg.hover}` }}
          >
            <Collapse in={isExpanded}>
              <Box sx={{ bgcolor: tokens.bg.hover }}>
                {documents.map((doc, index) => (
                  <Box
                    key={doc._id}
                    onClick={() => onDocumentClick?.(doc)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      pl: 7,
                      cursor: onDocumentClick ? "pointer" : "default",
                      borderBottom:
                        index < documents.length - 1
                          ? `1px solid ${tokens.border.light}`
                          : "none",
                      "&:hover": { bgcolor: tokens.bg.muted },
                    }}
                  >
                    <Description
                      sx={{ color: tokens.text.muted, mr: 1.5, fontSize: 18 }}
                    />

                    {/* Filename */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Tooltip title={doc.filename} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: tokens.fontSize.base,
                            fontWeight: tokens.fontWeight.semibold,
                            color: tokens.text.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                          }}
                        >
                          {doc.filename}
                        </Typography>
                      </Tooltip>
                    </Box>

                    {/* Document Type */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: tokens.fontSize.base,
                        color: tokens.text.secondary,
                        mr: 2,
                        minWidth: 100,
                      }}
                    >
                      {getDocumentTypeFullLabel(doc.documentType)}
                    </Typography>

                    {/* Status - only show if NOT ready */}
                    {doc.status !== "READY" && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: tokens.fontSize.base,
                          color:
                            doc.status === "FAILED" ? tokens.status.danger : tokens.text.secondary,
                          mr: 2,
                          minWidth: 80,
                        }}
                      >
                        {getStatusLabel(doc.status)}
                      </Typography>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                      {(doc.status === "READY" ||
                        doc.status === "TEXT_EXTRACTED" ||
                        doc.status === "KEYS_EXTRACTED") && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(doc);
                            }}
                            disabled={loadingStates.download.includes(doc._id)}
                            sx={{ color: tokens.text.primary, p: 0.5 }}
                          >
                            {loadingStates.download.includes(doc._id) ? (
                              <CircularProgress size={14} />
                            ) : (
                              <Download sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(doc);
                          }}
                          disabled={loadingStates.delete.includes(doc._id)}
                          sx={{ color: tokens.text.primary, p: 0.5 }}
                        >
                          {loadingStates.delete.includes(doc._id) ? (
                            <CircularProgress size={14} />
                          ) : (
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              style={{ width: 14, height: 14 }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
